import React, { useEffect, useState } from 'react';
import { useAuth } from '@hooks';
import { Navigate, useNavigate } from 'react-router-dom';
import { User, Settings, Heart, ShieldCheck, MessageSquare, Upload } from 'lucide-react';

const ProfilePage: React.FC = () => {
  const { user, logout, updateUserProfile, loading } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('profile');
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    address: user?.shippingAddress || '',
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        phone: user.phone || '',
        address: user.shippingAddress || '',
      });
    }

  }, [user]);


  if (!user && !loading) {
    return <Navigate to="/login" replace />;
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!user) {
      return;
    }

    updateUserProfile({
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      shippingAddress: formData.address,
    });
    setFormData({
      name: user.name,
      email: user.email,
      phone: user.phone || '',
      address: user.shippingAddress || '',
    });
    setEditMode(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="bg-gray-50 min-h-[calc(100vh-200px)]">
      <div className="container px-4 py-8 mx-auto">
        <div className="overflow-hidden bg-white shadow-md rounded-xl">
          <div className="md:flex">
            {/* Sidebar */}
            <div className="p-6 border-r border-gray-200 md:w-64 bg-gray-50">
              <div className="flex flex-col items-center mb-6">
                <div className="w-24 h-24 mb-3 overflow-hidden bg-gray-200 rounded-full">
                  {user?.avatar ? (
                    <img
                      src={user.avatar}
                      alt={user.name}
                      className="object-cover w-full h-full"
                    />
                  ) : (
                    <User size={96} className="p-6 text-gray-600" />
                  )}
                </div>
                <h2 className="text-xl font-bold text-gray-800">{user?.name}</h2>
                <p className="text-sm text-gray-500">{user?.email}</p>
              </div>

              <nav className="space-y-1">
                <button
                  onClick={() => setActiveTab('profile')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'profile'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  <User size={18} className="mr-3" />
                  Perfil
                </button>
                
                <button
                  onClick={() => setActiveTab('favorites')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'favorites'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  <Heart size={18} className="mr-3" />
                  Favoritos
                </button>
                <button
                  onClick={() => setActiveTab('messages')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'messages'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  <MessageSquare size={18} className="mr-3" />
                  Mensajes
                </button>
                <button
                  onClick={() => setActiveTab('security')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'security'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  <ShieldCheck size={18} className="mr-3" />
                  Seguridad
                </button>
                <button
                  onClick={() => setActiveTab('settings')}
                  className={`w-full flex items-center px-3 py-2 text-sm font-medium rounded-md ${activeTab === 'settings'
                      ? 'bg-blue-50 text-blue-700'
                      : 'text-gray-700 hover:bg-gray-100'
                    }`}
                >
                  <Settings size={18} className="mr-3" />
                  Configuración
                </button>
              </nav>

              <div className="pt-6 mt-6 border-t border-gray-200">
                <button
                  onClick={handleLogout}
                  className="w-full px-3 py-2 text-sm font-medium text-red-600 bg-white border border-red-300 rounded-md hover:bg-red-50"
                >
                  Cerrar sesión
                </button>
              </div>
            </div>

            {/* Main Content */}
            <div className="p-6 md:flex-1">
              {activeTab === 'profile' && (
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-2xl font-bold text-gray-800">Profile Information</h2>
                    {!editMode && (
                      <button
                        onClick={() => setEditMode(true)}
                        className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                      >
                        Editar Perfil
                      </button>
                    )}
                  </div>

                  {editMode ? (
                    <form onSubmit={handleSubmit}>
                      <div className="space-y-6">
                        <div>
                          <label className="block mb-1 text-sm font-medium text-gray-700">
                            Foto de Perfil
                          </label>
                          <div className="flex items-center">
                            <div className="w-16 h-16 mr-4 overflow-hidden bg-gray-200 rounded-full">
                              {user?.avatar ? (
                                <img
                                  src={user.avatar}
                                  alt={user.name}
                                  className="object-cover w-full h-full"
                                />
                              ) : (
                                <User size={64} className="p-4 text-gray-600" />
                              )}
                            </div>
                            <button
                              type="button"
                              className="bg-white border border-gray-300 rounded-md px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 flex items-center"
                            >
                              <Upload size={16} className="mr-1" />
                              Cambiar Foto
                            </button>
                          </div>
                        </div>

                        <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                          <div>
                            <label htmlFor="name" className="block mb-1 text-sm font-medium text-gray-700">
                              Nombre Completo
                            </label>
                            <input
                              type="text"
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div>
                            <label htmlFor="email" className="block mb-1 text-sm font-medium text-gray-700">
                              Correo Electrónico
                            </label>
                            <input
                              type="email"
                              id="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div>
                            <label htmlFor="phone" className="block mb-1 text-sm font-medium text-gray-700">
                              Número de Teléfono
                            </label>
                            <input
                              type="tel"
                              id="phone"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>

                          <div>
                            <label htmlFor="address" className="block mb-1 text-sm font-medium text-gray-700">
                              Dirección
                            </label>
                            <input
                              type="text"
                              id="address"
                              name="address"
                              value={formData.address}
                              onChange={handleChange}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                            />
                          </div>
                        </div>

                        <div className="flex space-x-3">
                          <button
                            type="submit"
                            className="px-4 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700"
                          >
                            Guardar Cambios
                          </button>
                          <button
                            type="button"
                            onClick={() => setEditMode(false)}
                            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50"
                          >
                            Cancelar
                          </button>
                        </div>
                      </div>
                    </form>
                  ) : (
                    <div className="p-6 rounded-lg bg-gray-50">
                      <dl className="divide-y divide-gray-200">
                        <div className="grid grid-cols-3 py-4">
                          <dt className="text-sm font-medium text-gray-500">Nombre Completo</dt>
                          <dd className="col-span-2 text-sm text-gray-900">{formData.name}</dd>
                        </div>
                        <div className="grid grid-cols-3 py-4">
                          <dt className="text-sm font-medium text-gray-500">Correo Electrónico</dt>
                          <dd className="col-span-2 text-sm text-gray-900">{formData.email}</dd>
                        </div>
                        <div className="grid grid-cols-3 py-4">
                          <dt className="text-sm font-medium text-gray-500">Número de Teléfono</dt>
                          <dd className="col-span-2 text-sm text-gray-900">{formData.phone}</dd>
                        </div>
                        <div className="grid grid-cols-3 py-4">
                          <dt className="text-sm font-medium text-gray-500">Dirección</dt>
                          <dd className="col-span-2 text-sm text-gray-900">{formData.address}</dd>
                        </div>
                        <div className="grid grid-cols-3 py-4">
                          <dt className="text-sm font-medium text-gray-500">Miembro Desde</dt>
                          <dd className="col-span-2 text-sm text-gray-900">March 15, 2023</dd>
                        </div>
                      </dl>
                    </div>
                  )}
                </div>
              )}

              {/* Sección de 'Mis Reservas' eliminada */}

              {activeTab === 'favorites' && (
                <div>
                  <h2 className="mb-6 text-2xl font-bold text-gray-800">Favoritos</h2>
                  <div className="flex flex-col items-center justify-center py-12">
                    <div className="p-4 mb-4 bg-blue-100 rounded-full">
                      <Heart size={40} className="text-blue-600" />
                    </div>
                    <h3 className="mb-2 text-xl font-semibold text-gray-800">Próximamente</h3>
                    <p className="max-w-md text-center text-gray-600">
                      Estamos trabajando en esta sección de favoritos y estará disponible pronto. Gracias por tu paciencia.
                    </p>
                  </div>
                </div>
              )}

              {(activeTab === 'messages' || activeTab === 'security' || activeTab === 'settings') && (
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="p-4 mb-4 bg-blue-100 rounded-full">
                    <Settings size={40} className="text-blue-600" />
                  </div>
                  <h3 className="mb-2 text-xl font-semibold text-gray-800">Próximamente</h3>
                  <p className="max-w-md text-center text-gray-600">
                    Estamos trabajando en esta funcionalidad y estará disponible pronto. ¡Gracias por tu paciencia!
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProfilePage;